import { Head, Link, router, usePoll } from '@inertiajs/react'
import { AudioLines, ChevronLeft, ChevronRight, LoaderCircle, Pencil, Play, Square } from 'lucide-react'
import { useEffect, useState } from 'react'
import { CharacterChip } from '@/components/character-chip'
import { LineEditor } from '@/components/line-editor'
import { PlaybackBar } from '@/components/playback-bar'
import { ScriptLine } from '@/components/script-line'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { usePlayerShortcuts } from '@/hooks/use-player-shortcuts'
import { useScenePlayer } from '@/hooks/use-scene-player'
import { adminProductions, adminScenes, adminSpeech } from '@/routes'
import type { Character, Production, Scene } from '@/types'

type SceneLink = { id: number; title: string }

type Props = {
  production: Production
  scene: Scene
  characters: Character[]
  previousScene: SceneLink | null
  nextScene: SceneLink | null
  /** AI takes in this scene still generating or processing; the page polls while > 0. */
  generatingCount: number
  ttsConfigured: boolean
}

export default function ShowScene({
  production,
  scene,
  characters,
  previousScene,
  nextScene,
  generatingCount,
  ttsConfigured,
}: Props) {
  const byId = new Map(characters.map((c) => [c.id, c]))
  const slug = { productionId: production.slug }
  const player = useScenePlayer(production.slug)
  const playing = player.sceneId === scene.id
  const [editingId, setEditingId] = useState<number | null>(null)

  // Opening a line to fix it pauses the scene where it is.
  const edit = (lineId: number) => {
    player.pause()
    setEditingId(lineId)
  }
  usePlayerShortcuts(player, { onStart: () => void player.play(scene.id), onEdit: edit })

  // An edit or a finished take: the player reloads the scene before it plays on.
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs whenever the scene prop is reloaded
  useEffect(() => player.invalidate(), [scene, player.invalidate])

  // After "Save & generate", play the line again as soon as its new take is
  // ready — if you're still paused on it.
  const [awaitingId, setAwaitingId] = useState<number | null>(null)
  useEffect(() => {
    const line = scene.lines.find((l) => l.id === awaitingId)
    if (!line || line.generating) return
    setAwaitingId(null)
    if (line.audioSource !== 'none' && player.status === 'paused' && player.current?.lineId === String(line.id))
      void player.replay()
  }, [scene, awaitingId, player])

  const poll = usePoll(3000, { only: ['scene', 'generatingCount'] }, { autoStart: false })
  useEffect(() => {
    generatingCount > 0 ? poll.start() : poll.stop()
  }, [generatingCount, poll])

  const playingLineId = player.current ? Number(player.current.lineId) : null
  useEffect(() => {
    if (playingLineId)
      document.getElementById(`line-${playingLineId}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [playingLineId])

  const spoken = scene.lines.filter((l) => l.kind === 'dialogue' || l.kind === 'song')
  const voiced = spoken.filter((l) => l.audioSource !== 'none').length
  const cast = [
    ...new Set(scene.lines.flatMap((l) => (l.characterId ? [l.characterId, ...l.coSpeakerIds] : []))),
  ].flatMap((id) => byId.get(id) ?? [])

  return (
    <div className="mx-auto grid w-full max-w-3xl gap-6">
      <Head title={`${scene.title} · ${production.title}`} />

      <div className="grid gap-3">
        <div className="flex items-center justify-between gap-2">
          <Link href={adminProductions.show(production.slug)} className="text-sm text-muted-foreground hover:underline">
            ← {production.title}
          </Link>
          <div className="flex gap-1">
            <SceneStep productionId={production.slug} scene={previousScene} direction="previous" />
            <SceneStep productionId={production.slug} scene={nextScene} direction="next" />
          </div>
        </div>

        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="grid gap-1">
            {scene.act && <p className="text-xs font-semibold uppercase text-muted-foreground">{scene.act}</p>}
            <h1 className="text-2xl font-semibold">{scene.title}</h1>
            <p className="text-sm text-muted-foreground">
              {voiced}/{spoken.length} lines voiced
              {generatingCount > 0 && (
                <span className="ml-2 inline-flex items-center gap-1 text-foreground">
                  <LoaderCircle className="size-3.5 animate-spin" /> generating {generatingCount}…
                </span>
              )}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" asChild>
              <Link href={adminScenes.edit({ ...slug, id: scene.id })}>
                <Pencil /> Edit
              </Link>
            </Button>
            <Button
              variant="outline"
              disabled={!ttsConfigured || voiced === spoken.length}
              onClick={() => router.visit(adminSpeech.create(slug, { query: { scene_id: scene.id } }))}
            >
              <AudioLines /> Generate voices
            </Button>
            <Button
              disabled={player.status === 'loading' || scene.lines.length === 0}
              onClick={() => (playing ? player.stop() : void player.play(scene.id))}
            >
              {playing ? <Square /> : <Play />}
              {playing ? 'Stop' : 'Play scene'}
            </Button>
          </div>
        </div>

        {cast.length > 0 && (
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {cast.map((c) => (
              <CharacterChip key={c.id} character={c} />
            ))}
          </div>
        )}
      </div>

      <Card>
        <CardContent className="grid gap-3">
          {scene.lines.map((line) => (
            <ScriptLine
              key={line.id}
              line={line}
              character={line.characterId ? byId.get(line.characterId) : undefined}
              coSpeakers={line.coSpeakerIds.flatMap((id) => byId.get(id) ?? [])}
              productionId={production.slug}
              playing={playingLineId === line.id}
              onPlayFrom={() => void player.play(scene.id, line.id)}
              onEdit={() => edit(line.id)}
              editor={
                editingId === line.id ? (
                  <LineEditor
                    line={line}
                    productionId={production.slug}
                    canGenerate={ttsConfigured && Boolean(line.characterId && byId.get(line.characterId)?.voiceId)}
                    onDone={(generated) => {
                      setEditingId(null)
                      if (generated) setAwaitingId(line.id)
                    }}
                  />
                ) : undefined
              }
            />
          ))}
          {scene.lines.length === 0 && (
            <p className="text-sm text-muted-foreground">No lines yet — edit the scene to add some.</p>
          )}
        </CardContent>
      </Card>

      {nextScene && (
        <Button variant="outline" className="justify-self-end" asChild>
          <Link href={adminScenes.show({ ...slug, id: nextScene.id })}>
            Next: {nextScene.title} <ChevronRight />
          </Link>
        </Button>
      )}

      <PlaybackBar player={player} productionId={production.slug} scenes={[scene]} onEdit={edit} />
    </div>
  )
}

function SceneStep({
  productionId,
  scene,
  direction,
}: {
  productionId: string
  scene: SceneLink | null
  direction: 'previous' | 'next'
}) {
  const Icon = direction === 'previous' ? ChevronLeft : ChevronRight
  const label = direction === 'previous' ? 'Previous scene' : 'Next scene'
  if (!scene)
    return (
      <Button variant="ghost" size="icon" disabled aria-label={label}>
        <Icon />
      </Button>
    )
  return (
    <Button variant="ghost" size="icon" asChild aria-label={label} title={`${label}: ${scene.title}`}>
      <Link href={adminScenes.show({ productionId, id: scene.id })}>
        <Icon />
      </Link>
    </Button>
  )
}
